import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcQuotemediaV1EodPriceJobQueryServiceEodPriceJobGet } from '@interxfi/api-client/generated/hooks/exc-quotemedia-v1-eod-price-job-query-service-eod-price-job-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table/custom-header';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcQuotemediaV1EodPriceJobState } from '@interxfi/types/generated/api';
import { TableSectionHeader } from '../../../../../../../../../../components/table/table-section-header';
import {
  QuoteMediaEodPriceJobEventDrawer,
  QuoteMediaEodPriceJobEventSummary,
} from '../../../../../../../../../../features/vendor/quotemedia/eod-price/job/quotemedia-eod-price-job-event';
import { getQuoteMediaEodPriceJobStateListItems } from '../../../../../../../../../../features/vendor/quotemedia/eod-price/job/quotemedia-eod-price-job-list-items';
import { getQuotemediaEodPriceJobDetailPath } from '../../../../../../../../../../features/vendor/quotemedia/eod-price/job/quotemedia-eod-price-job-routing';
import { QuotemediaEodPriceJobStatusPill } from '../../../../../../../../../../features/vendor/quotemedia/eod-price/job/quotemedia-eod-price-job-status-pill';
import { QuoteMediaEodPriceTasksTable } from '../../../../../../../../../../features/vendor/quotemedia/eod-price/task/quotemedia-eod-price-tasks-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/quote-media/job/_job/$jobId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `quotemedia-eod-price-job-${params.jobId}`,
        label: 'Job',
        to: getQuotemediaEodPriceJobDetailPath(params.jobId),
        suffix: <CopyButton content={params.jobId} label="Copy Job ID" tooltip={{ copy: params.jobId, copied: 'Copied Job ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('QuoteMedia EOD Price Job'),
  component: QuoteMediaEodPriceJobDetail,
});

function getListItems(data: ExcQuotemediaV1EodPriceJobState | undefined) {
  const items = getQuoteMediaEodPriceJobStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [items.data.spec.date, items.data.spec.assetCount],
        },
        {
          id: 'metrics',
          title: 'Metrics',
          items: [
            items.data.metrics.total,
            items.data.metrics.succeeded,
            items.data.metrics.errored,
            items.data.metrics.failed,
            items.data.metrics.pending,
          ],
        },
      ] as const satisfies ReadonlyListGroup[],
      right: [items.metadata.createdAt, items.metadata.updatedAt, items.jobId],
    },
  };
}

function getMenuItems(jobId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: QuoteMediaEodPriceJobEventDrawer,
        props: { request: { jobId } },
      },
    },
    actions: undefined,
  };
}

function TasksTableHeader(props: CustomTableHeaderProps) {
  return <TableSectionHeader {...props} title="Tasks" />;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load end of day price job', 'view this end of day price job');

function QuoteMediaEodPriceJobDetail() {
  const { jobId } = Route.useParams();
  const request = { jobId } as const;
  const { data, error, isPending } = useExcQuotemediaV1EodPriceJobQueryServiceEodPriceJobGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.eodPriceJob), [data?.eodPriceJob]);
  const menuItems = useMemo(() => getMenuItems(jobId), [jobId]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <QuotemediaEodPriceJobStatusPill isPending={showPending} size="large" status={data?.eodPriceJob?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <QuoteMediaEodPriceJobEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <QuoteMediaEodPriceTasksTable HeaderActions={TasksTableHeader} jobId={jobId} />
      </PageLayout.Section>
    </PageLayout>
  );
}
